// Local
import HeadingOne from "../../components/HeadingOne";
import BaseButtonPrimary from "../../components/BaseButtonPrimary";
import BaseButtonSecondary from "../../components/BaseButtonSecondary";
import { ImportStep } from "./QuickbooksImportContainer";
import { ExistingGLAccount } from "../../models/gl-account";

interface Props {
  siteKey: string;
  isConnected: boolean;
  currentStep: ImportStep;
  currentStepIndex: number;
  totalSteps: number;
  onNextStep: () => void;
  onPrevStep: () => void;
  onOpenImportAccountsDialog: () => void;
  isLoadingQBOAccounts: boolean;
  gLAccountList: ExistingGLAccount[];
  onImportAllCustomers: () => Promise<void>;
  isImportingCustomers: boolean;
  onImportAllItems: () => Promise<void>;
  isImportingItems: boolean;
  onImportAllInvoicesAndPayments: () => Promise<void>;
  isImportingInvoicesAndPayments: boolean;
  qboRecordCounts: Record<string, number> | null;
  isLoadingRecordCounts: boolean;
  stiltRecordCounts: Record<string, number> | null;
  isLoadingStiltRecordCounts: boolean;

  children: {
    connectionSection: React.ReactNode; // For when QB is not connected
    customersImportProgress: React.ReactNode;
    itemsImportProgress: React.ReactNode;
    invoicesAndPaymentsImportProgress: React.ReactNode;

    // The following are for the old UI structure and might be phased out or repurposed
    // importOptionButtons: React.ReactNode;
    // optionAllButtons: React.ReactNode;
    // optionSingleButtons: React.ReactNode;

    // Content for OLD specific import types (will be integrated into new steps)
    importAllContent: React.ReactNode; // e.g. part of customer/invoice steps
    importSingleByCustomerContent: React.ReactNode;
    importSingleAllInvoicesContent: React.ReactNode;
  };
}

const STEP_DETAILS: Array<{ id: ImportStep; title: string }> = [
  {
    id: "accounts",
    title: "Import Accounts",
  },
  {
    id: "pricebook",
    title: "Import Price Book",
  },
  {
    id: "customers",
    title: "Import Customers",
  },
  {
    id: "invoices_payments",
    title: "Import Invoices & Payments",
  },
];

export default function QuickbooksImportPage(props: Props) {
  const progressPercentage =
    ((props.currentStepIndex + 1) / props.totalSteps) * 100;

  const alreadyImportedAccounts = props.gLAccountList.filter(
    (acc) => acc.customData?.accountingRefID,
  );

  const renderStepContent = () => {
    switch (props.currentStep) {
      case "accounts":
        return (
          <div className="space-y-4 rounded-lg bg-white p-6 shadow-md">
            <h3 className="text-xl font-semibold text-gray-700">
              {STEP_DETAILS.find((s) => s.id === "accounts")?.title}
            </h3>
            <p className="text-gray-600">
              First, let's import the necessary accounts from your QuickBooks
              Chart of Accounts. We recommend importing:
            </p>
            <ul className="list-inside list-disc space-y-1 text-gray-600">
              <li>
                <strong>Undeposited Funds:</strong> This account is used to hold
                payments that haven't been deposited yet and is usually already
                created for you in QuickBooks.
              </li>
              <li>
                <strong>Bank Accounts:</strong> Any checking or savings accounts
                where deposits are made. This will allow you to reconcile your
                credit card, check, and cash deposits against payments that have
                been recorded in Stilt.
              </li>
              <li>
                <strong>Sales / Income Accounts:</strong> Any accounts you use
                to track income from services or product sales. These will be
                linked to your Price Book Items in Stilt.
              </li>
            </ul>
            <p className="text-sm text-gray-500">
              You generally do not need to import other account types like
              expenses, assets (other than bank accounts), liabilities, or
              equity for basic Stilt functionality.
            </p>
            <BaseButtonPrimary
              onClick={props.onOpenImportAccountsDialog}
              isBusy={props.isLoadingQBOAccounts}
              disabled={props.isLoadingQBOAccounts}
            >
              {props.isLoadingQBOAccounts
                ? "Loading Accounts..."
                : "Select QuickBooks Accounts to Import"}
            </BaseButtonPrimary>

            {alreadyImportedAccounts.length > 0 && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-700">
                  Already Imported Accounts:
                </h4>
                <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-gray-600">
                  {alreadyImportedAccounts.map((acc) => (
                    <li key={acc.id}>{acc.name}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        );
      case "pricebook": {
        const qboItemCount =
          props.qboRecordCounts?.items ??
          props.qboRecordCounts?.Items ??
          props.qboRecordCounts?.pricebookitems ??
          0;
        const stiltItemCount =
          props.stiltRecordCounts?.items ??
          props.stiltRecordCounts?.Items ??
          props.stiltRecordCounts?.pricebookitems ??
          0;

        const qboCountText = props.isLoadingRecordCounts
          ? "Loading QuickBooks item count..."
          : `${qboItemCount} Price Book Item${qboItemCount !== 1 ? "s" : ""}.`;
        const stiltCountText = props.isLoadingStiltRecordCounts
          ? "Loading Stilt item count..."
          : `${stiltItemCount} Price Book Item${stiltItemCount !== 1 ? "s" : ""}.`;
        return (
          <div className="space-y-4 rounded-lg bg-white p-6 shadow-md">
            <h3 className="text-xl font-semibold text-gray-700">
              {STEP_DETAILS.find((s) => s.id === "pricebook")?.title}
            </h3>
            <p className="text-gray-600">
              Next, import your products and services from QuickBooks. These
              will become Price Book Items in Stilt, which you can then use to
              create estimates and invoices.
            </p>
            {/* Combined Counts Display */}
            <div className="mt-2 rounded-md bg-gray-100 p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 md:flex-nowrap">
                <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                  <img
                    src="/quickbooks-2.svg"
                    alt="QuickBooks"
                    className="mr-2 h-5 w-5 flex-shrink-0"
                  />
                  <div>
                    <span className="font-semibold">QuickBooks:</span>{" "}
                    {qboCountText}
                  </div>
                </div>
                <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                  <img
                    src="/black_yellow_hardhat_transparent_bg.svg"
                    alt="Stilt"
                    className="mr-2 h-5 w-5 flex-shrink-0"
                  />
                  <div>
                    <span className="font-semibold">Stilt:</span>{" "}
                    {stiltCountText}
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-4 rounded-md border border-dashed border-gray-300 bg-gray-50 p-8 text-center">
              {/* <p className="text-gray-500">
                [Please contact support for help importing your price book
                items.]
              </p> */}
              <BaseButtonPrimary
                // className="mt-4"
                // disabled={
                //   (qboItemCount === 0 && !props.isLoadingRecordCounts) ||
                //   props.isLoadingStiltRecordCounts
                // }
                onClick={props.onImportAllItems}
                isBusy={props.isImportingItems}
                busyText="Importing Price Book Items..."
              >
                Import Price Book Items
              </BaseButtonPrimary>
              {props.children.itemsImportProgress}
            </div>
          </div>
        );
      }
      case "customers": {
        const qboCustomerCount =
          props.qboRecordCounts?.customers ??
          props.qboRecordCounts?.Customers ??
          0;
        const stiltCustomerCount =
          props.stiltRecordCounts?.customers ??
          props.stiltRecordCounts?.Customers ??
          0;

        const qboCountText = props.isLoadingRecordCounts
          ? "Loading QuickBooks customer count..."
          : `${qboCustomerCount} customer${qboCustomerCount !== 1 ? "s" : ""}`;
        const stiltCountText = props.isLoadingStiltRecordCounts
          ? "Loading Stilt customer count..."
          : `${stiltCustomerCount} customer${stiltCustomerCount !== 1 ? "s" : ""}`;
        return (
          <div className="space-y-4 rounded-lg bg-white p-6 shadow-md">
            <h3 className="text-xl font-semibold text-gray-700">
              {STEP_DETAILS.find((s) => s.id === "customers")?.title}
            </h3>
            <p className="text-gray-600">
              Now, let's bring in your entire customer list from QuickBooks.
              This will create new customer records in Stilt for any QuickBooks
              customers not already present. Existing customers linked to
              QuickBooks will not be affected by this initial import.
            </p>
            {/* Combined Counts Display */}
            <div className="mt-2 rounded-md bg-gray-100 p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 md:flex-nowrap">
                <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                  <img
                    src="/quickbooks-2.svg"
                    alt="QuickBooks"
                    className="mr-2 h-5 w-5 flex-shrink-0"
                  />
                  <div>
                    <span className="font-semibold">QuickBooks:</span>{" "}
                    {qboCountText}
                  </div>
                </div>
                <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                  <img
                    src="/black_yellow_hardhat_transparent_bg.svg"
                    alt="Stilt"
                    className="mr-2 h-5 w-5 flex-shrink-0"
                  />
                  <div>
                    <span className="font-semibold">Stilt:</span>{" "}
                    {stiltCountText}
                  </div>
                </div>
              </div>
            </div>
            <BaseButtonPrimary
              onClick={props.onImportAllCustomers}
              isBusy={props.isImportingCustomers}
              busyText="Importing Customers..."
              // disabled={
              //   props.isImportingCustomers || // <- this one DEF isn't needed - it's covered by `isBusy`
              //   (qboCustomerCount === 0 && !props.isLoadingRecordCounts) ||
              //   props.isLoadingStiltRecordCounts
              // }
            >
              Import All Customers from QuickBooks
            </BaseButtonPrimary>
            <p className="mt-2 text-sm text-gray-500">
              Note: This process might take a few moments if you have a large
              number of customers.
            </p>
            {props.children.customersImportProgress}
          </div>
        );
      }
      case "invoices_payments": {
        const qboInvoiceCount =
          props.qboRecordCounts?.invoices ??
          props.qboRecordCounts?.Invoices ??
          0;
        const qboPaymentCount =
          props.qboRecordCounts?.payments ??
          props.qboRecordCounts?.Payments ??
          0;
        const stiltInvoiceCount =
          props.stiltRecordCounts?.invoices ??
          props.stiltRecordCounts?.Invoices ??
          0;
        const stiltPaymentCount =
          props.stiltRecordCounts?.payments ??
          props.stiltRecordCounts?.Payments ??
          0;

        const qboCountTextInvoices = props.isLoadingRecordCounts
          ? "Loading QuickBooks invoice count..."
          : `${qboInvoiceCount} invoice${qboInvoiceCount !== 1 ? "s" : ""}.`;
        const qboCountTextPayments = props.isLoadingRecordCounts
          ? "Loading QuickBooks payment count..."
          : `${qboPaymentCount} payment${qboPaymentCount !== 1 ? "s" : ""}.`;
        const stiltCountTextInvoices = props.isLoadingStiltRecordCounts
          ? "Loading Stilt invoice count..."
          : `${stiltInvoiceCount} invoice${stiltInvoiceCount !== 1 ? "s" : ""}.`;
        const stiltCountTextPayments = props.isLoadingStiltRecordCounts
          ? "Loading Stilt payment count..."
          : `${stiltPaymentCount} payment${stiltPaymentCount !== 1 ? "s" : ""}.`;

        return (
          <div className="space-y-4 rounded-lg bg-white p-6 shadow-md">
            <h3 className="text-xl font-semibold text-gray-700">
              {STEP_DETAILS.find((s) => s.id === "invoices_payments")?.title}
            </h3>
            <p className="text-gray-600">
              Finally, import your invoices and their associated payments from
              QuickBooks. This will help sync your historical data.
            </p>
            {/* Combined Counts Display for Invoices and Payments */}
            <div className="mt-2 space-y-3">
              <div className="rounded-md bg-gray-100 p-4 shadow-sm">
                <p className="mb-2 text-center text-sm font-medium text-gray-600">
                  Invoices
                </p>
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 md:flex-nowrap">
                  <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                    <img
                      src="/quickbooks-2.svg"
                      alt="QuickBooks"
                      className="mr-2 h-5 w-5 flex-shrink-0"
                    />
                    <div>
                      <span className="font-semibold">QuickBooks:</span>{" "}
                      {qboCountTextInvoices}
                    </div>
                  </div>
                  <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                    <img
                      src="/black_yellow_hardhat_transparent_bg.svg"
                      alt="Stilt"
                      className="mr-2 h-5 w-5 flex-shrink-0"
                    />
                    <div>
                      <span className="font-semibold">Stilt:</span>{" "}
                      {stiltCountTextInvoices}
                    </div>
                  </div>
                </div>
              </div>
              <div className="rounded-md bg-gray-100 p-4 shadow-sm">
                <p className="mb-2 text-center text-sm font-medium text-gray-600">
                  Payments
                </p>
                <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2 md:flex-nowrap">
                  <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                    <img
                      src="/quickbooks-2.svg"
                      alt="QuickBooks"
                      className="mr-2 h-5 w-5 flex-shrink-0"
                    />
                    <div>
                      <span className="font-semibold">QuickBooks:</span>{" "}
                      {qboCountTextPayments}
                    </div>
                  </div>
                  <div className="flex min-w-[200px] flex-1 items-center text-sm text-gray-700">
                    <img
                      src="/black_yellow_hardhat_transparent_bg.svg"
                      alt="Stilt"
                      className="mr-2 h-5 w-5 flex-shrink-0"
                    />
                    <div>
                      <span className="font-semibold">Stilt:</span>{" "}
                      {stiltCountTextPayments}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* <p className="mt-4 text-gray-500">
              [Please contact support for help importing your invoices and
              payments.]
            </p> */}
            <BaseButtonPrimary
              // className="mt-4"
              // disabled={
              //   (qboInvoiceCount === 0 &&
              //     qboPaymentCount === 0 &&
              //     !props.isLoadingRecordCounts) ||
              //   props.isLoadingStiltRecordCounts
              // }
              onClick={props.onImportAllInvoicesAndPayments}
              isBusy={props.isImportingInvoicesAndPayments}
              busyText="Importing Invoices & Payments..."
            >
              Import Invoices & Payments
            </BaseButtonPrimary>
            {props.children.invoicesAndPaymentsImportProgress}
          </div>
        );
      }
      default:
        return <p>Unknown step.</p>;
    }
  };

  if (!props.isConnected) {
    return (
      <div className="mx-auto max-w-screen-lg space-y-12 p-8">
        {/* QuickBooks Online Connection Section */}
        <div className="rounded-lg bg-white p-8 text-center shadow-xl">
          <img
            src="/quickbooks-2.svg"
            alt="QuickBooks Logo"
            className="mx-auto mb-6 h-20 w-auto"
          />
          <HeadingOne className="mb-4 text-3xl font-bold text-gray-800">
            Connect to QuickBooks Online
          </HeadingOne>
          <p className="mb-8 text-lg text-gray-600">
            To import your data, you first need to securely connect your
            QuickBooks Online account. This will allow Stilt to import your
            customers, items, invoices, and payments.
          </p>
          <div className="flex justify-center">
            {props.children.connectionSection}
          </div>
          <p className="mt-8 text-sm text-gray-500">
            Stilt uses secure OAuth to connect to QuickBooks. We never store
            your QuickBooks credentials.
          </p>
        </div>

        {/* QuickBooks Desktop Support Section */}
        <div className="rounded-lg bg-gray-50 p-8 text-center shadow-lg">
          <img
            src="/quickbooks-2.svg"
            alt="QuickBooks Logo"
            className="mx-auto mb-6 h-20 w-auto"
          />
          <h2 className="mb-4 text-2xl font-semibold text-gray-700">
            Using QuickBooks Desktop?
          </h2>
          <p className="mb-6 text-gray-600">
            If you use QuickBooks Desktop, please contact our support team for
            assistance with setting up your integration. We'll help you get your
            data synced with Stilt.
          </p>
          <BaseButtonSecondary
            onClick={() =>
              window.open(
                "mailto:support@stiltwork.com?subject=QuickBooks%20Desktop%20Integration%20Assistance",
                "_blank",
              )
            }
            type="button"
            className="inline-flex items-center"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="mr-2 h-5 w-5"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
            Email Support for Desktop
          </BaseButtonSecondary>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-screen-xl space-y-8 p-4 md:p-8">
      {/* Header when connected */}
      <div className="flex flex-wrap items-center justify-between gap-4 rounded-lg border-2 border-green-500 bg-white p-6 shadow-md">
        <div className="flex items-center gap-4">
          <img
            src="/quickbooks-2.svg"
            alt="QuickBooks Logo"
            className="h-12 w-auto"
          />
          <HeadingOne className="text-2xl font-semibold text-gray-800">
            QuickBooks Data Import
          </HeadingOne>
        </div>
        <div className="text-right">
          {props.children.connectionSection} {/* Allow disconnect/reconnect */}
        </div>
      </div>

      {/* Progress Bar and Stepper */}
      <div className="mb-8 rounded-lg bg-white p-6 shadow-md">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">Import Progress</h2>
          <span className="text-sm font-medium text-gray-600">
            Step {props.currentStepIndex + 1} of {props.totalSteps} (
            {Math.round(progressPercentage)}%)
          </span>
        </div>
        <div className="mb-4 h-3 w-full rounded-full bg-gray-200">
          <div
            className="h-3 rounded-full bg-green-500 transition-all duration-500 ease-out"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
        {/* Stepper Indicator */}
        <div className="mt-6 flex justify-between">
          {STEP_DETAILS.map((step, index) => (
            <div
              key={step.id}
              className="flex flex-col items-center text-center"
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-full border-2 ${
                  index === props.currentStepIndex
                    ? "border-green-500 bg-green-500 text-white"
                    : index < props.currentStepIndex
                      ? "border-green-500 bg-green-100 text-green-700"
                      : "border-gray-300 bg-gray-100 text-gray-500"
                }`}
              >
                {index < props.currentStepIndex ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={2.5}
                    stroke="currentColor"
                    className="h-6 w-6"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M4.5 12.75l6 6 9-13.5"
                    />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              <p
                className={`mt-2 text-xs font-medium ${
                  index === props.currentStepIndex
                    ? "text-green-600"
                    : "text-gray-500"
                }`}
              >
                {step.title}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Current Step Content */}
      <div className="min-h-[300px]">{renderStepContent()}</div>

      {/* Navigation Buttons */}
      <div className="mt-8 flex justify-between rounded-lg bg-white p-6 shadow-md">
        <BaseButtonSecondary
          onClick={props.onPrevStep}
          disabled={props.currentStepIndex === 0}
        >
          Previous Step
        </BaseButtonSecondary>
        <BaseButtonPrimary onClick={props.onNextStep}>
          {props.currentStepIndex === props.totalSteps - 1
            ? "Finish Import Setup"
            : "Next Step"}
        </BaseButtonPrimary>
      </div>

      {/* Old UI (for reference, to be removed/integrated) */}
      {/*
      <div className="mx-auto mb-2 max-w-fit space-y-3 rounded-md border border-gray-400 bg-gray-100 px-4 pb-3.5 pt-2 xs:mx-0">
        <p className="text-center">There are two main ways to import data:</p>
        <div className="flex flex-col items-center justify-center gap-4 xs:flex-row xs:gap-8">
          {props.children.importOptionButtons}
        </div>
      </div>

      {props.importOption === "all" && (
        <>
          <h2 className="mb-1 text-lg font-medium italic text-gray-500">
            Import Entire Sets...
          </h2>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            {props.children.optionAllButtons}
          </div>

          <h3 className="mt-2 text-lg font-medium italic text-gray-500">
            {getH3Text(props.selectedDataTypeAll)}
          </h3>
        </>
      )}

      {props.importOption === "single" && (
        <>
          <h2 className="mb-1 text-lg font-medium italic text-gray-500">
            Import Select Records...
          </h2>
          <div className="flex flex-wrap justify-center gap-x-12 gap-y-4">
            {props.children.optionSingleButtons}
          </div>

          <h3 className="mt-2 text-lg font-medium italic text-gray-500">
            {props.selectedDataTypeSingle === "byCustomer"
              ? "Import by Customer..."
              : "Import by Invoice..."}
          </h3>
        </>
      )}

      {props.importOption === "all" && props.children.importAllContent}

      {props.importOption === "single" &&
        props.selectedDataTypeSingle === "allInvoices" &&
        props.children.importSingleAllInvoicesContent}

      {props.importOption === "single" &&
        props.selectedDataTypeSingle === "byCustomer" &&
        props.children.importSingleByCustomerContent}
      */}
    </div>
  );
}

/* Commented out old helper, can be removed once new structure is stable
function getH3Text(dataType: DataTypeImportAll): string {
  switch (dataType) {
    case "customers":
      return "Import All Customers...";
    case "invoices":
      return "Import All Invoices...";
    case "pbItems":
      return "Import All PriceBook Items...";
    case "payments":
      return "Import All Payments...";
    case "all":
      return "Import All Data...";
    default:
      const exhaustiveCheck: never = dataType;
      return exhaustiveCheck;
  }
}
*/
